import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import PartyModal from "./PartyModel";
import "./party.scss";

// Using a standard icon set (Lucide or React-Icons)
import {
  FaPlus,
  FaEdit,
  FaTrashAlt,
  FaHandshake,
  FaSearch,
  FaStore,
  FaUserTie,
  FaSpinner,
} from "react-icons/fa";

const PartyPage = () => {
  const [parties, setParties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // UI State
  const [showModal, setShowModal] = useState(false);
  const [editingParty, setEditingParty] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("All"); // 'All', 'Retailer', 'Customer'

  const fetchParties = async () => {
    setIsLoading(true);
    try {
      const snap = await getDocs(collection(db, "parties"));
      setParties(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error("Error fetching parties:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchParties();
  }, []);

  const handleSave = () => {
    fetchParties();
    setEditingParty(null);
    setShowModal(false);
  };

  const handleEdit = (party) => {
    setEditingParty(party);
    setShowModal(true);
  };

  const handleDelete = async (partyId, partyName) => {
    if (
      window.confirm(
        `⚠️ PERMANENT ACTION: Are you sure you want to delete ${partyName}? This may affect linked orders.`,
      )
    ) {
      try {
        await deleteDoc(doc(db, "parties", partyId));
        setParties((prev) => prev.filter((p) => p.id !== partyId));
      } catch (error) {
        console.error("Error deleting party:", error);
        alert("Failed to delete party. Check console.");
      }
    }
  };

  const toggleStatus = async (party) => {
    const newStatus = party.status === "Active" ? "Inactive" : "Active";
    try {
      await updateDoc(doc(db, "parties", party.id), { status: newStatus });
      fetchParties();
    } catch (error) {
      alert("Failed to update status.");
    }
  };

  // Derived Data for ERP
  const filteredParties = useMemo(() => {
    return parties.filter((p) => {
      const matchesSearch =
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.gstNumber &&
          p.gstNumber.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesType = filterType === "All" || p.type === filterType;
      return matchesSearch && matchesType;
    });
  }, [parties, searchTerm, filterType]);

  const metrics = {
    total: parties.length,
    retailers: parties.filter((p) => p.type === "Retailer").length,
    customers: parties.filter((p) => p.type === "Customer").length,
  };

  return (
    <div className="erp-party-module">
      {/* 1. Header & Metrics */}
      <div className="erp-page-header">
        <div className="header-title">
          <FaHandshake className="title-icon" />
          <div>
            <h1>Party Master Directory</h1>
            <p>Manage Retailers (B2B portals) and Direct Customers.</p>
          </div>
        </div>
      </div>

      <div className="erp-metrics-grid">
        <div className="metric-card">
          <div className="mc-info">
            <span>Total Parties</span>
            <h3>{metrics.total}</h3>
          </div>
          <FaHandshake className="mc-icon all" />
        </div>
        <div className="metric-card">
          <div className="mc-info">
            <span>Retailers (B2B)</span>
            <h3>{metrics.retailers}</h3>
          </div>
          <FaStore className="mc-icon retailer" />
        </div>
        <div className="metric-card">
          <div className="mc-info">
            <span>Direct Customers</span>
            <h3>{metrics.customers}</h3>
          </div>
          <FaUserTie className="mc-icon customer" />
        </div>
      </div>

      {/* 2. Controls & Toolbar */}
      <div className="erp-toolbar">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by Name or GST..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-tabs">
          {["All", "Retailer", "Customer"].map((type) => (
            <button
              key={type}
              className={filterType === type ? "active" : ""}
              onClick={() => setFilterType(type)}
            >
              {type}s
            </button>
          ))}
        </div>

        <button
          className="erp-btn-primary"
          onClick={() => {
            setEditingParty(null);
            setShowModal(true);
          }}
        >
          <FaPlus /> Onboard New Party
        </button>
      </div>

      {/* 3. Data Table */}
      <div className="erp-table-container">
        {isLoading ? (
          <div className="erp-loading-state">
            <FaSpinner className="spinner" /> Loading Database...
          </div>
        ) : filteredParties.length === 0 ? (
          <div className="erp-empty-state">No parties match your criteria.</div>
        ) : (
          <table className="erp-data-table">
            <thead>
              <tr>
                <th>Entity Name</th>
                <th>Type</th>
                <th>Contact Info</th>
                <th>GST / Tax ID</th>
                <th>Portal Access</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredParties.map((p) => (
                <tr key={p.id}>
                  <td className="fw-bold">{p.name}</td>
                  <td>
                    <span className={`type-badge ${p.type?.toLowerCase()}`}>
                      {p.type === "Retailer" ? <FaStore /> : <FaUserTie />}{" "}
                      {p.type}
                    </span>
                  </td>
                  <td className="contact-cell">
                    <div>{p.phone || "No Phone"}</div>
                    <div className="text-muted">{p.email || "No Email"}</div>
                  </td>
                  <td className="font-mono">{p.gstNumber || "Unregistered"}</td>
                  <td>
                    {p.type === "Retailer" ? (
                      <span className="access-badge granted">Enabled</span>
                    ) : (
                      <span className="access-badge none">N/A</span>
                    )}
                  </td>
                  <td>
                    <button
                      className={`status-toggle ${p.status?.toLowerCase()}`}
                      onClick={() => toggleStatus(p)}
                      title="Click to toggle status"
                    >
                      {p.status || "Active"}
                    </button>
                  </td>
                  <td>
                    <div className="erp-action-cell">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(p)}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(p.id, p.name)}
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 4. Modal Portal */}
      {showModal && (
        <PartyModal
          party={editingParty}
          onClose={() => {
            setShowModal(false);
            setEditingParty(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default PartyPage;
